import type { SlotProps } from "@radix-ui/react-slot";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@acdc2/ui/lib/utils";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

type Props = SlotProps & {
  onMove: (movementX: number, movementY: number) => void;
  onDone: () => void;
  disabled?: boolean;
};

export function Moveable({
  onMove,
  onDone,
  disabled,
  children,
  className,
  ...props
}: Props) {
  const [isDragged, setIsDragged] = useState(false);
  const [pressedKeys, setPressedKeys] = useState<Set<ArrowKey>>(new Set());
  const debouncedDone = useDebouncedCallback(onDone, 1000);

  const onKeyDown = async (event: React.KeyboardEvent<HTMLElement>) => {
    if (ARROW_KEYS.includes(event.key)) {
      event.stopPropagation();

      const newPressedKeys = new Set(pressedKeys);
      newPressedKeys.add(event.key);
      setPressedKeys(newPressedKeys);

      let movementX = 0;
      let movementY = 0;

      if (newPressedKeys.has("ArrowLeft")) movementX -= 1;
      if (newPressedKeys.has("ArrowRight")) movementX += 1;
      if (newPressedKeys.has("ArrowUp")) movementY -= 1;
      if (newPressedKeys.has("ArrowDown")) movementY += 1;

      onMove(movementX, movementY);
      debouncedDone();
    }
  };

  const onKeyUp = async (event: React.KeyboardEvent<HTMLElement>) => {
    if (ARROW_KEYS.includes(event.key)) {
      event.stopPropagation();

      const newPressedKeys = new Set(pressedKeys);
      newPressedKeys.delete(event.key);
      setPressedKeys(newPressedKeys);
    }
  };

  const onMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    event.stopPropagation();
    setIsDragged(true);
  };

  useEffect(() => {
    if (!isDragged) return;

    const onMouseMove = (event: MouseEvent) => {
      if (event.buttons !== 1) {
        setIsDragged(false);
        return;
      }

      onMove(event.movementX, event.movementY);
    };

    const onMouseUp = () => {
      setIsDragged(false);
      debouncedDone();
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isDragged, onMove, debouncedDone]);

  return (
    <Slot {...props}>
      <Slot
        className={cn({ "is-dragged": isDragged }, className)}
        onMouseDown={onMouseDown}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        tabIndex={0}
      >
        {children}
      </Slot>
    </Slot>
  );
}

const ARROW_KEYS = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];

type ArrowKey = (typeof ARROW_KEYS)[number];
